import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UsersService } from '../../../../core/services/user/users.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RolesService } from '../../../../core/services/user/roles.service';
import { SpecialismsService } from '../../../../core/services/user/specialisms.service';
import { CustomersService } from '../../../../core/services/customer/customers.service';
import { lettersOnlyValidator } from '../../../shared/validators/validators';
import { RoleListModel } from '../../../../core/models/user/role-list.model';
import { CustomerListModel } from '../../../../core/models/customer/customer-list.model';
import { SpecialismListModel } from '../../../../core/models/user/specialism-list.model';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { ToastService } from '../../../../core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationBranchListModel } from '../../../../core/models/user/organization-branch-list.model';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { TranslateModule } from '@ngx-translate/core';
import { UserRole } from '../../../../../enums/roles.enum';
import { OverlayService } from '../../../../core/services/overlay.service';
@Component({
  selector: 'app-create-edit-user',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './create-edit-user.component.html',
  styleUrl: './create-edit-user.component.scss',
})
export class CreateEditUserComponent {
  userForm: FormGroup;
  roles: RoleListModel[] = [];
  userId: string = '';
  specialismList: SpecialismListModel[] = [];
  organizationList: CustomerListModel[] = [];
  organizationBranchList: OrganizationBranchListModel[] = [];
  maxDate: string;
  currentUser: UserSessionModel | null = null;
  isOverlayLoading: boolean = false;
  selectedBranch: OrganizationBranchListModel | null = null;
  userRole = UserRole;

  constructor(
    private router: Router,
    private usersService: UsersService,
    private rolesService: RolesService,
    private specialismsService: SpecialismsService,
    private customersService: CustomersService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private translate: TranslateService,
    private overlayService: OverlayService
  ) {
    const userLang = 'tr';
    this.translate.use(userLang);

    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.userForm = this.fb.group({
      specialisms: [''],
      roleId: [null, Validators.required],
      name: ['', [Validators.required, lettersOnlyValidator()]],
      surname: ['', [Validators.required, lettersOnlyValidator()]],
      pid: ['', [Validators.required, Validators.minLength(6)]],
      dateOfBirth: ['', Validators.required],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern('^\\+?[0-9]{7,15}$')],
      ],
      email: ['', [Validators.required, Validators.email]],
      branchCode: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.getUserById();
    this.currentUser = this.currentUserService.GetSession();
    this.getSpecialisms();
    this.getRoles();
    this.getOrganizations();

    this.getBranchesByOrganizationCode(this.currentUser.customerCode);
  }

  getUserById() {
    this.userId = this.route.snapshot.paramMap.get('id')!;
    if (this.userId) {
      this.usersService.getUser(this.userId).subscribe({
        next: (res) => {
          this.userForm.patchValue({
            name: res.name,
            surname: res.surname,
            email: res.email,
            phoneNumber: res.phoneNumber,
            pid: res.pid,
            roleId: res.roleId,
            branchCode: res.branchCode,
            dateOfBirth: res.dateOfBirth.slice(0, 10),
            specialisms: res.userSpecialsms.map(
              (item: any) => item.specialismId
            ),
          });
        },
      });
    }
  }

  getSpecialisms() {
    this.specialismsService.getSpecialisms().subscribe({
      next: (res) => {
        this.specialismList = res;
      },
    });
  }

  getRoles() {
    const allowedRoles = [
      this.userRole.GeneralPractitioner,
      this.userRole.Assistant,
      this.userRole.OrganizationAdmin,
      this.userRole.Internist,
    ];

    this.rolesService.getRoles().subscribe({
      next: (res) => {
        this.roles = res.items.filter((element) =>
          allowedRoles.includes(element.name)
        );

        this.roles.forEach((role) => {
          role.name = this.translate.instant(role.name);
        });
      },
    });
  }

  getOrganizations() {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString();
  }

  saveUser(e: Event) {
    e.preventDefault();
    if (this.userId) {
      // Edit user
      if (this.userForm.valid) {
        this.userForm.value.dateOfBirth = this.formatEditDate(
          this.userForm.value.dateOfBirth
        );

        const formatUserSpecialisms = [
          {
            id: this.specialismList.find(
              (specialism: SpecialismListModel) =>
                specialism.name === 'Diabetese'
            ).id,
          },
        ];
        const formData = {
          ...this.userForm.value,
          id: this.userId,
          userSpecialisms: formatUserSpecialisms,
          customerCode: this.currentUser?.customerCode,
          branchName: this.currentUser?.branchName,
          branchSelectedLanguageCode:
            this.currentUser?.branchSelectedLanguageCode,
          sendReportToPatient: this.currentUser?.sendReportToPatient,
        };
        delete formData.specialisms;

        this.overlayService.show();

        this.usersService.editUser(this.userId, formData).subscribe({
          next: () => {
            this.isOverlayLoading = true;
            this.toastService.showToast(
              this.translate.instant('successEditUserDesc'),
              'success'
            );
            setTimeout(() => {
              this.router.navigate(['/user/list']);
              this.overlayService.hide();
            }, 1000);
          },
          complete: () => {
            setTimeout(() => {
              this.overlayService.hide();
              this.router.navigate(['/user/list']);
            }, 2000);
          },
        });
      } else {
        this.toastService.showToast(
          this.translate.instant('warningAllFieldsAreRequiredDesc'),
          'warning'
        );
        this.userForm.markAllAsTouched();
      }
    } else {
      // Create new user
      if (this.userForm.valid) {
        this.userForm.value.dateOfBirth = this.formatDate(
          this.userForm.value.dateOfBirth
        );

        const formatUserSpecialisms = [
          {
            id: this.specialismList.find(
              (specialism) => specialism.name === 'Diabetese'
            ).id,
          },
        ];

        const formData = {
          ...this.userForm.value,
          specialisms: formatUserSpecialisms,
          customerCode: this.currentUser?.customerCode,
          branchCode: this.selectedBranch?.id,
          branchName: this.selectedBranch?.name,
          branchSelectedLanguageCode: this.selectedBranch?.languageCode,
          sendReportToPatient: this.currentUser?.sendReportToPatient,
        };

        this.overlayService.show();
        this.usersService.createUser(formData).subscribe({
          next: () => {
            this.isOverlayLoading = true;
            this.toastService.showToast(
              this.translate.instant('successCreateUserDesc'),
              'success'
            );
            setTimeout(() => {
              this.router.navigate(['/user/list']);
              this.overlayService.hide();
            }, 1000);
          },

          complete: () => {
            setTimeout(() => {
              this.router.navigate(['/user/list']);
              this.overlayService.hide();
            }, 2000);
          },
        });
      } else {
        this.toastService.showToast(
          this.translate.instant('warningAllFieldsAreRequiredDesc'),
          'warning'
        );
        this.userForm.markAllAsTouched();
      }
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.userForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onChangeSelectedBranch(event: any) {
    const selectedBranchId = (event.target as HTMLSelectElement).value;
    this.selectedBranch = this.organizationBranchList.find(
      (item: OrganizationBranchListModel) => item.id == selectedBranchId
    );
  }
  getBranchesByOrganizationCode(customerCode: string) {
    this.customersService
      .getCustomerBranchesByCustomerCode(customerCode)
      .subscribe({
        next: (res) => {
          this.organizationBranchList = res;
        },
      });
  }

  formatEditDate(dateString: string): string {
    const [day, month, year] = dateString.split('-').map(Number);
    const selectedDate = new Date(Date.UTC(year, month - 1, day));
    return selectedDate.toISOString();
  }

  preventForbiddenCharacters(
    event: Event,
    type: 'number' | 'text' | 'email' | 'tel'
  ): void {
    const inputElement = event.target as HTMLInputElement;
    let val: string | number = inputElement.value;
    const isInvalidNumber = (value: number) =>
      isNaN(value) || value < (type === 'number' ? 1 : 0);

    if (type === 'number' || type === 'tel') {
      val = Number(inputElement.value);
      if (isInvalidNumber(val)) {
        inputElement.value = '';
        return;
      }
    } else if (type === 'text') {
      if (/^[\s]|[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/.test(val)) {
        inputElement.value = val.replace(/^[\s]+|[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '');
      }
      if (inputElement.value.trim() === '') {
        inputElement.value = '';
      }
    } else if (type === 'email') {
      if (/^[\s]|[^a-zA-Z0-9@._]/.test(val)) {
        inputElement.value = val.replace(/^[\s]+|[^a-zA-Z0-9@._]/g, '');
      }
      if (inputElement.value.trim() === '') {
        inputElement.value = '';
      }
    }
  }
}
